import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { useAuth } from '../context/AuthContext';

const ProfilePage: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [avatar, setAvatar] = useState<string>(user?.avatarUrl || '');
  const [saving, setSaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const initials = useMemo(() => {
    const n = user?.name || '';
    if (!n) return '?';
    const parts = n.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }, [user?.name]);

  const startEdit = () => {
    setName(user?.name || '');
    setAvatar(user?.avatarUrl || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    setSaving(true);
    try {
      await updateProfile({ name, avatar });
      setEditing(false);
      setSnackOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Avatar src={(editing ? avatar : user?.avatarUrl) || undefined} sx={{ width: 96, height: 96, fontSize: 32 }}>
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, width: '100%' }}>
              {!editing && (
                <>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>{user?.name || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">{user?.email || '-'}</Typography>
                </>
              )}
              {editing && (
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: { xs: 2, sm: 0 } }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Name"
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                    <TextField
                      label="Avatar URL"
                      fullWidth
                      value={avatar}
                      onChange={(e) => setAvatar(e.target.value)}
                    />
                  </Stack>
                </Box>
              )}
            </Box>
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          {!editing && (
            <IconButton color="primary" onClick={startEdit} aria-label="edit profile">
              <Typography component="span" variant="body1">✎</Typography>
            </IconButton>
          )}
          {editing && (
            <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
              <Button variant="outlined" onClick={cancelEdit} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={handleSubmit as any} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </Stack>
          )}
        </CardActions>
      </Card>
      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        onClose={() => setSnackOpen(false)}
        message="Profile updated"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ProfilePage;
